import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit2, Trash2, Eye, Package, ShoppingCart, Tag, Truck, Settings, CreditCard, Bell, X, Check, ChevronDown, ExternalLink } from "lucide-react";
import { http, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import MediaUploader from "../../components/MediaUploader";

const TABS = [
  { id: "products", label: "Products", icon: Package },
  { id: "collections", label: "Collections", icon: Tag },
  { id: "orders", label: "Orders", icon: ShoppingCart },
  { id: "shipping", label: "Shipping", icon: Truck },
  { id: "discounts", label: "Discounts", icon: Tag },
  { id: "payment", label: "Payment", icon: CreditCard },
  { id: "settings", label: "Store Settings", icon: Settings },
  { id: "emails", label: "Email Templates", icon: Bell },
];

const ORDER_STATUSES = ["pending", "paid", "processing", "shipped", "delivered", "cancelled"];

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 text-lg">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors"><X size={18} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      {children}
    </div>
  );
}

function Inp({ ...props }) {
  return <input {...props} className={`w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-blue-400 transition-colors ${props.className || ""}`} />;
}

function Sel({ children, ...props }) {
  return <select {...props} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none bg-white">{children}</select>;
}

// ── Products Tab ──────────────────────────────────────────────────────────────
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [variantRows, setVariantRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      http.get("/admin/store/products").then(r => setProducts(r.data)).catch(() => {}),
      http.get("/store/collections").then(r => setCollections(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ name: "", description: "", price: "", compare_price: "", sku: "", stock: "", collection_id: "", badge: "", images: [], media: [], featured: false });
    setVariantRows([]);
    setModal("new");
  };

  const openEdit = (p) => {
    const media = (p.images || []).map(url => ({ url, type: "image" }));
    setForm({ ...p, images: p.images || [], media });
    setVariantRows(p.variants || []);
    setModal("edit");
  };

  const save = async () => {
    if (!form.name || !form.price) { toast.error("Name and price are required"); return; }
    setSaving(true);
    try {
      const imageUrls = (form.media || []).map(m => m.url);
      const payload = {
        name: form.name,
        description: form.description || "",
        price: Number(form.price) || 0,
        compare_price: form.compare_price ? Number(form.compare_price) : null,
        sku: form.sku || "",
        stock: form.stock !== "" ? Number(form.stock) : null,
        collection_id: form.collection_id || null,
        badge: form.badge || "",
        featured: !!form.featured,
        variants: variantRows.map(v => ({
          group: v.group || "Size",
          label: v.label || "",
          price: v.price ? Number(v.price) : null,
          stock: v.stock !== "" ? Number(v.stock) : null,
        })),
        images: imageUrls
      };
      console.log("Sending payload:", payload);
      if (modal === "new") await http.post("/admin/store/products", payload);
      else await http.put(`/admin/store/products/${form.id}`, payload);
      toast.success("Product saved!");
      setModal(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await http.delete(`/admin/store/products/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const addVariant = () => setVariantRows(r => [...r, { id: `v_${Date.now()}`, label: "", price: "", stock: "", group: "Size" }]);
  const setVR = (idx, k, v) => setVariantRows(r => r.map((row, i) => i === idx ? { ...row, [k]: v } : row));
  const delVR = (idx) => setVariantRows(r => r.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">{products.length} products</div>
        <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-white text-sm" style={{ background: "#0F1E38" }}><Plus size={15} /> New Product</button>
      </div>
      {loading ? <div className="animate-pulse space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 rounded-xl bg-gray-100" />)}</div>
      : products.length === 0 ? <div className="text-center py-12 text-gray-400">No products yet. Create your first product.</div>
      : (
        <div className="space-y-2">
          {products.map(p => (
            <div key={p.id} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-50 shrink-0">
                {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 truncate">{p.name}</div>
                <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-3">
                  <span>${Number(p.price).toFixed(2)}</span>
                  {p.stock !== undefined && <span>Stock: {p.stock}</span>}
                  {p.collection_name && <span>{p.collection_name}</span>}
                  {p.featured && <span className="text-yellow-600 font-semibold">★ Featured</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link to={`/store/product/${p.id}`} target="_blank" className="p-2 text-gray-400 hover:text-blue-500 transition-colors"><ExternalLink size={14} /></Link>
                <button onClick={() => openEdit(p)} className="p-2 text-gray-400 hover:text-blue-600 transition-colors"><Edit2 size={14} /></button>
                <button onClick={() => del(p.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && (
        <Modal title={modal === "new" ? "New Product" : "Edit Product"} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" required><Inp value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Product name" /></Field>
              <Field label="SKU"><Inp value={form.sku || ""} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} placeholder="SKU-001" /></Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Price ($)" required><Inp type="number" value={form.price || ""} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="29.99" /></Field>
              <Field label="Compare Price ($)"><Inp type="number" value={form.compare_price || ""} onChange={e => setForm(f => ({ ...f, compare_price: e.target.value }))} placeholder="39.99" /></Field>
              <Field label="Stock"><Inp type="number" value={form.stock || ""} onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} placeholder="100" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Collection">
                <Sel value={form.collection_id || ""} onChange={e => setForm(f => ({ ...f, collection_id: e.target.value }))}>
                  <option value="">None</option>
                  {collections.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Sel>
              </Field>
              <Field label="Badge"><Inp value={form.badge || ""} onChange={e => setForm(f => ({ ...f, badge: e.target.value }))} placeholder="New, Sale, etc." /></Field>
            </div>
            <Field label="Description">
              <textarea value={form.description || ""} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={4} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-blue-400 resize-none" placeholder="Product description…" />
            </Field>
            <Field label="Product Images">
              <MediaUploader mediaUrls={form.media || []} onChange={media => setForm(f => ({ ...f, media }))} />
            </Field>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="featured" checked={!!form.featured} onChange={e => setForm(f => ({ ...f, featured: e.target.checked }))} className="accent-yellow-500" />
              <label htmlFor="featured" className="text-sm font-semibold text-gray-700">Featured product</label>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Variants / Sizes</div>
                <button onClick={addVariant} className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"><Plus size={13} /> Add Variant</button>
              </div>
              {variantRows.length === 0 && <div className="text-xs text-gray-400">No variants — single product sold as-is.</div>}
              {variantRows.map((v, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <Inp value={v.group || ""} onChange={e => setVR(i, "group", e.target.value)} placeholder="Group (Size/Color)" className="w-28" />
                  <Inp value={v.label || ""} onChange={e => setVR(i, "label", e.target.value)} placeholder="Label (S, Red…)" className="flex-1" />
                  <Inp type="number" value={v.price || ""} onChange={e => setVR(i, "price", e.target.value)} placeholder="Price" className="w-20" />
                  <Inp type="number" value={v.stock || ""} onChange={e => setVR(i, "stock", e.target.value)} placeholder="Stock" className="w-20" />
                  <button onClick={() => delVR(i)} className="p-1.5 text-gray-400 hover:text-red-500"><X size={13} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setModal(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 font-semibold text-sm text-gray-700">Cancel</button>
              <button onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm disabled:opacity-50" style={{ background: "#0F1E38" }}>{saving ? "Saving…" : "Save Product"}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Collections Tab ────────────────────────────────────────────────────────────
function CollectionsTab() {
  const [cols, setCols] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", description: "", image_url: "", media: [] });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => http.get("/admin/store/collections").then(r => setCols(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (c) => {
    const media = c.image_url ? [{ url: c.image_url, type: "image" }] : [];
    setForm({ ...c, media });
    setEditId(c.id);
    setModal(true);
  };
  const openNew = () => { setForm({ name: "", slug: "", description: "", image_url: "", media: [] }); setEditId(null); setModal(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        slug: form.slug,
        description: form.description || "",
        image_url: (form.media || []).length > 0 ? form.media[0].url : "",
      };
      if (editId) await http.put(`/admin/store/collections/${editId}`, payload);
      else await http.post("/admin/store/collections", payload);
      toast.success("Saved"); setModal(false); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-white text-sm" style={{ background: "#0F1E38" }}><Plus size={15} /> New Collection</button>
      </div>
      <div className="space-y-2">
        {cols.map(c => (
          <div key={c.id} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
            {c.image_url && <img src={c.image_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />}
            <div className="flex-1"><div className="font-semibold text-gray-900">{c.name}</div><div className="text-xs text-gray-400">/{c.slug}</div></div>
            <div className="flex gap-2">
              <button onClick={() => openEdit(c)} className="p-1.5 text-gray-400 hover:text-blue-600"><Edit2 size={14} /></button>
              <button onClick={async () => { if (!window.confirm("Delete?")) return; await http.delete(`/admin/store/collections/${c.id}`).catch(() => {}); load(); }} className="p-1.5 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      {modal && (
        <Modal title={editId ? "Edit Collection" : "New Collection"} onClose={() => setModal(false)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" required><Inp value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value, slug: f.slug || e.target.value.toLowerCase().replace(/\s+/g, "-") }))} /></Field>
              <Field label="Slug" required><Inp value={form.slug || ""} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} /></Field>
            </div>
            <Field label="Description"><textarea value={form.description || ""} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none resize-none" /></Field>
            <Field label="Cover Image">
              <MediaUploader mediaUrls={form.media || []} onChange={media => setForm(f => ({ ...f, media }))} />
            </Field>
            <div className="flex gap-3">
              <button onClick={() => setModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700">Cancel</button>
              <button onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm disabled:opacity-50" style={{ background: "#0F1E38" }}>{saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Orders Tab ─────────────────────────────────────────────────────────────────
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    http.get("/admin/store/orders").then(r => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const updateStatus = async (id, status, tracking) => {
    setUpdating(id);
    try { await http.put(`/admin/store/orders/${id}`, { status, tracking_number: tracking }); toast.success("Order updated"); setOrders(o => o.map(x => x.id === id ? { ...x, status, tracking_number: tracking } : x)); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setUpdating(null); }
  };

  const STATUS_COLORS = { pending: "bg-yellow-50 text-yellow-700", paid: "bg-blue-50 text-blue-700", processing: "bg-purple-50 text-purple-700", shipped: "bg-indigo-50 text-indigo-700", delivered: "bg-green-50 text-green-700", cancelled: "bg-red-50 text-red-700" };

  if (loading) return <div className="animate-pulse space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 rounded-xl bg-gray-100" />)}</div>;

  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-500">{orders.length} orders</div>
      {orders.map(o => (
        <div key={o.id} className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-bold text-gray-900">#{o.order_number || o.id?.slice(-8).toUpperCase()}</div>
              <div className="text-xs text-gray-500 mt-0.5">{o.shipping_address?.name} · {o.shipping_address?.email}</div>
              <div className="text-xs text-gray-400">{new Date(o.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
            </div>
            <div className="text-right">
              <div className="font-bold text-gray-900">${Number(o.total).toFixed(2)}</div>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[o.status] || STATUS_COLORS.pending}`}>{o.status?.toUpperCase()}</span>
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
            <Sel value={o.status || "pending"} onChange={e => updateStatus(o.id, e.target.value, o.tracking_number)} className="flex-1 text-xs">
              {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </Sel>
            <Inp value={o.tracking_number || ""} onChange={e => setOrders(ords => ords.map(x => x.id === o.id ? { ...x, tracking_number: e.target.value } : x))} placeholder="Tracking #" className="flex-1 text-xs" />
            <button onClick={() => updateStatus(o.id, o.status, o.tracking_number)} disabled={updating === o.id} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold disabled:opacity-50">
              {updating === o.id ? "…" : "Save"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Shipping Tab ───────────────────────────────────────────────────────────────
function ShippingTab() {
  const [zones, setZones] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", price: "", estimated_days: "", countries: "" });
  const [editId, setEditId] = useState(null);

  const load = () => http.get("/admin/store/shipping-zones").then(r => setZones(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        estimated_days: form.estimated_days || "",
      };
      if (editId) await http.put(`/admin/store/shipping-zones/${editId}`, payload);
      else await http.post("/admin/store/shipping-zones", payload);
      toast.success("Saved"); setModal(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => { setForm({ name: "", description: "", price: "", estimated_days: "", countries: "" }); setEditId(null); setModal(true); }} className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-white text-sm" style={{ background: "#0F1E38" }}><Plus size={15} /> New Zone</button>
      </div>
      {zones.map(z => (
        <div key={z.id} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
          <div className="flex-1">
            <div className="font-semibold text-gray-900">{z.name}</div>
            <div className="text-xs text-gray-400 mt-0.5">{z.estimated_days} · ${Number(z.price).toFixed(2)} {z.price === 0 ? "(Free)" : ""}</div>
          </div>
          <button onClick={() => { setForm(z); setEditId(z.id); setModal(true); }} className="p-1.5 text-gray-400 hover:text-blue-600"><Edit2 size={14} /></button>
          <button onClick={async () => { await http.delete(`/admin/store/shipping-zones/${z.id}`).catch(() => {}); load(); }} className="p-1.5 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
        </div>
      ))}
      {modal && (
        <Modal title={editId ? "Edit Shipping Zone" : "New Shipping Zone"} onClose={() => setModal(false)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Zone Name" required><Inp value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Standard Shipping" /></Field>
              <Field label="Price ($)"><Inp type="number" value={form.price || ""} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="0 = Free" /></Field>
            </div>
            <Field label="Estimated Delivery"><Inp value={form.estimated_days || ""} onChange={e => setForm(f => ({ ...f, estimated_days: e.target.value }))} placeholder="3-5 business days" /></Field>
            <Field label="Description"><Inp value={form.description || ""} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></Field>
            <Field label="Countries (comma-separated, leave blank for all)"><Inp value={form.countries || ""} onChange={e => setForm(f => ({ ...f, countries: e.target.value }))} placeholder="Nigeria, Ghana, Kenya" /></Field>
            <div className="flex gap-3"><button onClick={() => setModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm">Cancel</button><button onClick={save} className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm" style={{ background: "#0F1E38" }}>Save</button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Payment Settings Tab ───────────────────────────────────────────────────────
function PaymentTab() {
  const [settings, setSettings] = useState({ stripe_enabled: false, stripe_publishable_key: "", stripe_secret_key: "", paypal_enabled: false, paypal_client_id: "", paypal_secret: "" });
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState({});

  useEffect(() => { http.get("/admin/store/payment-settings").then(r => setSettings(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        stripe_enabled: !!settings.stripe_enabled,
        stripe_publishable_key: settings.stripe_publishable_key || "",
        stripe_secret_key: settings.stripe_secret_key || "",
        paypal_enabled: !!settings.paypal_enabled,
        paypal_client_id: settings.paypal_client_id || "",
        paypal_secret: settings.paypal_secret || "",
      };
      await http.put("/admin/store/payment-settings", payload);
      toast.success("Payment settings saved!");
    }
    catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const toggleShow = (k) => setShow(s => ({ ...s, [k]: !s[k] }));

  const SecretField = ({ label, field }) => (
    <Field label={label}>
      <div className="relative">
        <Inp type={show[field] ? "text" : "password"} value={settings[field] || ""} onChange={e => setSettings(s => ({ ...s, [field]: e.target.value }))} placeholder={show[field] ? "" : "••••••••••••••••"} className="pr-20" />
        <button type="button" onClick={() => toggleShow(field)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400 hover:text-gray-600">{show[field] ? "Hide" : "Show"}</button>
      </div>
    </Field>
  );

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
        API keys are stored securely in the database and never exposed to the frontend. The publishable key (Stripe) and client ID (PayPal) are served to the checkout form. Secret keys are used only server-side.
      </div>
      {/* Stripe */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <div className="flex items-center gap-3">
          <CreditCard size={18} className="text-indigo-500" />
          <div className="font-bold text-gray-900">Stripe</div>
          <label className="ml-auto flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!settings.stripe_enabled} onChange={e => setSettings(s => ({ ...s, stripe_enabled: e.target.checked }))} className="accent-indigo-500 w-4 h-4" />
            <span className="text-sm font-semibold text-gray-700">Enable</span>
          </label>
        </div>
        <SecretField label="Publishable Key (pk_…)" field="stripe_publishable_key" />
        <SecretField label="Secret Key (sk_…)" field="stripe_secret_key" />
        <div className="text-xs text-gray-400">Get keys from dashboard.stripe.com → Developers → API Keys</div>
      </div>
      {/* PayPal */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <div className="flex items-center gap-3">
          <span className="text-blue-600 font-bold text-sm">PP</span>
          <div className="font-bold text-gray-900">PayPal</div>
          <label className="ml-auto flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!settings.paypal_enabled} onChange={e => setSettings(s => ({ ...s, paypal_enabled: e.target.checked }))} className="accent-blue-500 w-4 h-4" />
            <span className="text-sm font-semibold text-gray-700">Enable</span>
          </label>
        </div>
        <SecretField label="Client ID" field="paypal_client_id" />
        <SecretField label="Secret" field="paypal_secret" />
        <div className="text-xs text-gray-400">Get credentials from developer.paypal.com → My Apps & Credentials</div>
      </div>
      <button onClick={save} disabled={saving} className="px-8 py-3 rounded-xl font-bold text-white disabled:opacity-50" style={{ background: "#0F1E38" }}>{saving ? "Saving…" : "Save Payment Settings"}</button>
    </div>
  );
}

// ── Store Settings Tab ─────────────────────────────────────────────────────────
function StoreSettingsTab() {
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { http.get("/admin/store/settings").then(r => setSettings(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setSettings(s => ({ ...s, [k]: v }));
  const setHero = (k, v) => setSettings(s => ({ ...s, hero: { ...(s.hero || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        store_name: settings.store_name || "CPM Store",
        logo_url: settings.logo_url || "",
        currency: settings.currency || "$",
        announcement_banner: settings.announcement_banner || "",
        hero: settings.hero || {},
        shipping_info_lines: settings.shipping_info_lines || [],
        badge_shipping: settings.badge_shipping || "",
        badge_shipping_sub: settings.badge_shipping_sub || "",
        badge_secure: settings.badge_secure || "",
        badge_returns: settings.badge_returns || "",
        badge_returns_sub: settings.badge_returns_sub || "",
        badge_quality: settings.badge_quality || "",
      };
      await http.put("/admin/store/settings", payload);
      toast.success("Store settings saved!");
    }
    catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <div className="font-bold text-gray-900 mb-2">General</div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Store Name"><Inp value={settings.store_name || ""} onChange={e => set("store_name", e.target.value)} placeholder="CPM Store" /></Field>
          <Field label="Currency Symbol"><Inp value={settings.currency || ""} onChange={e => set("currency", e.target.value)} placeholder="$" /></Field>
        </div>
        <Field label="Logo URL"><Inp value={settings.logo_url || ""} onChange={e => set("logo_url", e.target.value)} placeholder="https://…" /></Field>
        <Field label="Announcement Banner"><Inp value={settings.announcement_banner || ""} onChange={e => set("announcement_banner", e.target.value)} placeholder="Free shipping on orders over $50 🎉" /></Field>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <div className="font-bold text-gray-900 mb-2">Hero Section</div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Heading"><Inp value={settings.hero?.heading || ""} onChange={e => setHero("heading", e.target.value)} placeholder="Shop the CPM Collection" /></Field>
          <Field label="Label (tag line)"><Inp value={settings.hero?.label || ""} onChange={e => setHero("label", e.target.value)} placeholder="Alleluia ✞" /></Field>
        </div>
        <Field label="Subheading"><textarea value={settings.hero?.subheading || ""} onChange={e => setHero("subheading", e.target.value)} rows={2} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none resize-none" /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="CTA Button Label"><Inp value={settings.hero?.cta_label || ""} onChange={e => setHero("cta_label", e.target.value)} placeholder="Shop Now" /></Field>
          <Field label="Background Color"><Inp type="color" value={settings.hero?.bg_color || "#0F1E38"} onChange={e => setHero("bg_color", e.target.value)} /></Field>
        </div>
        <Field label="Hero Background Image URL"><Inp value={settings.hero?.image_url || ""} onChange={e => setHero("image_url", e.target.value)} /></Field>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <div className="font-bold text-gray-900 mb-2">Shipping Info Lines (shown on product page)</div>
        {[0, 1, 2].map(i => (
          <Inp key={i} value={(settings.shipping_info_lines || [])[i] || ""} onChange={e => { const lines = [...(settings.shipping_info_lines || ["", "", ""])]; lines[i] = e.target.value; set("shipping_info_lines", lines); }} placeholder={`Shipping info line ${i + 1}`} />
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <div className="font-bold text-gray-900 mb-2">Trust Badges</div>
        <div className="grid grid-cols-2 gap-4">
          {["badge_shipping", "badge_shipping_sub", "badge_secure", "badge_returns", "badge_returns_sub", "badge_quality"].map(k => (
            <Field key={k} label={k.replace(/_/g, " ")}><Inp value={settings[k] || ""} onChange={e => set(k, e.target.value)} /></Field>
          ))}
        </div>
      </div>
      <button onClick={save} disabled={saving} className="px-8 py-3 rounded-xl font-bold text-white disabled:opacity-50" style={{ background: "#0F1E38" }}>{saving ? "Saving…" : "Save Settings"}</button>
    </div>
  );
}

// ── Discounts Tab ──────────────────────────────────────────────────────────────
function DiscountsTab() {
  const [codes, setCodes] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ code: "", type: "percent", value: "", min_order: "", max_uses: "", expires_at: "" });
  const load = () => http.get("/admin/store/discount-codes").then(r => setCodes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      const payload = {
        ...form,
        value: Number(form.value) || 0,
        min_order: form.min_order ? Number(form.min_order) : null,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
      };
      await http.post("/admin/store/discount-codes", payload);
      toast.success("Code created"); setModal(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => { setForm({ code: "", type: "percent", value: "", min_order: "", max_uses: "", expires_at: "" }); setModal(true); }} className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-white text-sm" style={{ background: "#0F1E38" }}><Plus size={15} /> New Code</button>
      </div>
      {codes.map(c => (
        <div key={c.id} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
          <code className="font-bold text-sm bg-gray-100 px-3 py-1 rounded-lg">{c.code}</code>
          <div className="flex-1 text-sm text-gray-600">{c.type === "percent" ? `${c.value}% off` : `$${c.value} off`}{c.min_order ? ` · min $${c.min_order}` : ""}</div>
          <div className="text-xs text-gray-400">{c.uses || 0} uses{c.max_uses ? ` / ${c.max_uses}` : ""}</div>
          <button onClick={async () => { await http.delete(`/admin/store/discount-codes/${c.id}`).catch(() => {}); load(); }} className="p-1.5 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
        </div>
      ))}
      {modal && (
        <Modal title="New Discount Code" onClose={() => setModal(false)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Code" required><Inp value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="SAVE20" /></Field>
              <Field label="Type"><Sel value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}><option value="percent">Percentage (%)</option><option value="fixed">Fixed amount ($)</option></Sel></Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Value" required><Inp type="number" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} placeholder="20" /></Field>
              <Field label="Min Order ($)"><Inp type="number" value={form.min_order} onChange={e => setForm(f => ({ ...f, min_order: e.target.value }))} /></Field>
              <Field label="Max Uses"><Inp type="number" value={form.max_uses} onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))} /></Field>
            </div>
            <Field label="Expires At"><Inp type="date" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} /></Field>
            <div className="flex gap-3"><button onClick={() => setModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm">Cancel</button><button onClick={save} className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm" style={{ background: "#0F1E38" }}>Create Code</button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Email Templates Tab ────────────────────────────────────────────────────────
function EmailTemplatesTab() {
  const [templates, setTemplates] = useState({});
  const [saving, setSaving] = useState(false);
  const TEMPLATE_KEYS = [
    { key: "order_confirmation_subject", label: "Order Confirmation: Subject" },
    { key: "order_confirmation_body", label: "Order Confirmation: Body", multiline: true },
    { key: "shipping_update_subject", label: "Shipping Update: Subject" },
    { key: "shipping_update_body", label: "Shipping Update: Body", multiline: true },
    { key: "admin_new_order_email", label: "Admin New Order Alert: Email(s)", hint: "Comma-separated emails for new order notifications" },
    { key: "admin_low_stock_email", label: "Admin Low Stock Alert: Email(s)" },
    { key: "admin_low_stock_threshold", label: "Low Stock Threshold" },
  ];
  useEffect(() => { 
    http.get("/admin/store/email-templates").then(r => {
      const data = r.data || {};
      // Set default admin email if not configured
      if (!data.admin_new_order_email) {
        data.admin_new_order_email = "sunday@isunday.me";
      }
      // Set default email templates if not configured
      if (!data.order_confirmation_subject) {
        data.order_confirmation_subject = "Order Confirmed - #{order_number} | CPM Store";
      }
      if (!data.order_confirmation_body) {
        data.order_confirmation_body = `Dear {customer_name},

Thank you for your order! We're pleased to confirm that we've received your order #{order_number}.

Order Details:
- Total: ${total}
- Status: Processing

You'll receive another email when your order ships with tracking information.

Blessings,
CPM Store Team
Celestial People Meet`;
      }
      if (!data.shipping_update_subject) {
        data.shipping_update_subject = "Your Order Has Shipped! - #{order_number} | CPM Store";
      }
      if (!data.shipping_update_body) {
        data.shipping_update_body = `Dear {customer_name},

Great news! Your order #{order_number} has been shipped and is on its way to you.

Tracking Number: {tracking_number}

You can track your package using the tracking number above. Expected delivery time varies by location.

Thank you for shopping with CPM Store. We appreciate your support of the Celestial community.

Blessings,
CPM Store Team`;
      }
      if (!data.admin_low_stock_threshold) {
        data.admin_low_stock_threshold = "10";
      }
      setTemplates(data);
    }).catch(() => {}); 
  }, []);
  const save = async () => {
    setSaving(true);
    try { await http.put("/admin/store/email-templates", templates); toast.success("Email templates saved!"); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-2xl space-y-4">
      <div className="text-sm text-gray-500 bg-yellow-50 border border-yellow-100 rounded-xl p-3">Use <code className="bg-yellow-100 px-1 rounded">{"{order_number}"}</code>, <code className="bg-yellow-100 px-1 rounded">{"{customer_name}"}</code>, <code className="bg-yellow-100 px-1 rounded">{"{total}"}</code>, <code className="bg-yellow-100 px-1 rounded">{"{tracking_number}"}</code> as dynamic variables.</div>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        {TEMPLATE_KEYS.map(({ key, label, multiline, hint }) => (
          <Field key={key} label={label}>
            {multiline
              ? <textarea value={templates[key] || ""} onChange={e => setTemplates(t => ({ ...t, [key]: e.target.value }))} rows={4} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none resize-none" />
              : <Inp value={templates[key] || ""} onChange={e => setTemplates(t => ({ ...t, [key]: e.target.value }))} />
            }
            {hint && <div className="text-xs text-gray-400 mt-1">{hint}</div>}
          </Field>
        ))}
      </div>
      <button onClick={save} disabled={saving} className="px-8 py-3 rounded-xl font-bold text-white disabled:opacity-50" style={{ background: "#0F1E38" }}>{saving ? "Saving…" : "Save Templates"}</button>
    </div>
  );
}

// ── Embeddable inner content (used inside central Admin console) ───────────────
export function StoreAdminContent() {
  const [activeTab, setActiveTab] = useState("products");
  const CONTENT = { products: <ProductsTab />, collections: <CollectionsTab />, orders: <OrdersTab />, shipping: <ShippingTab />, discounts: <DiscountsTab />, payment: <PaymentTab />, settings: <StoreSettingsTab />, emails: <EmailTemplatesTab /> };
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-1.5 pb-3 border-b border-gray-100">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === id ? "bg-[#0F1E38] text-white" : "text-gray-600 hover:bg-gray-100 border border-gray-200"
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>
      <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
        <div className="mb-4 font-bold text-base text-gray-800 flex items-center gap-2">
          {(() => { const t = TABS.find(t => t.id === activeTab); return t ? <><t.icon size={16} className="text-gray-500" /> {t.label}</> : null; })()}
        </div>
        {CONTENT[activeTab]}
      </div>
    </div>
  );
}

// ── Root StoreAdmin ────────────────────────────────────────────────────────────
export default function StoreAdmin() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("products");

  if (!user || (user.role !== "super_admin" && user.role !== "parish_admin")) {
    return <div className="text-center py-20 text-gray-500">Access restricted to administrators.</div>;
  }

  const CONTENT = { products: <ProductsTab />, collections: <CollectionsTab />, orders: <OrdersTab />, shipping: <ShippingTab />, discounts: <DiscountsTab />, payment: <PaymentTab />, settings: <StoreSettingsTab />, emails: <EmailTemplatesTab /> };

  return (
    <div className="min-h-screen" style={{ background: "#F8F9FA" }}>
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/store" className="text-sm font-semibold text-yellow-600 hover:text-yellow-700">← Store</Link>
            <span className="text-gray-300">·</span>
            <span className="font-bold text-gray-900">Store Admin</span>
          </div>
          <Link to="/app/admin" className="text-sm text-gray-500 hover:text-gray-700">Main Admin →</Link>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-6">
        <aside className="w-48 shrink-0">
          <nav className="space-y-1">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => setActiveTab(id)} className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors text-left ${activeTab === id ? "text-white" : "text-gray-600 hover:bg-gray-100"}`} style={activeTab === id ? { background: "#0F1E38" } : {}}>
                <Icon size={15} />{label}
              </button>
            ))}
          </nav>
        </aside>
        <div className="flex-1 min-w-0">
          <div className="mb-4 font-bold text-lg text-gray-900">{TABS.find(t => t.id === activeTab)?.label}</div>
          {CONTENT[activeTab]}
        </div>
      </div>
    </div>
  );
}
